import React, { useEffect } from "react"
import ReactDOM from "react-dom"
import { XMarkIcon } from "@heroicons/react/24/outline"
import classNames from "../../utils/classNames"
import ErrorBoundary from "../ErrorBoundary"
import { ModalProvider } from "~/contexts/admin/ModalProvider"

interface ComponentProps {
  children?: React.ReactNode
  open: boolean
  onClose: () => void
  className?: string
  maxWidth?: string
  isDynamicWidth?: boolean
  customPadding?: string
  closeClassNames?: string
  isXMarkHidden?: boolean
  width?: string
}

export default function Modal(props: ComponentProps) {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        props.onClose()
      }
    }

    if (props.open) {
      document.addEventListener("keydown", handleEscapeKey)
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey)
    }
  }, [props.open, props.onClose])
  if (!props.open) return null

  const modalContent = (
    <ErrorBoundary
      description="Something unexpected happened, please refresh this page."
      ctaLabel="Refresh page"
      ctaOnClick={() => window.location.reload()}
    >
      <ModalProvider onClose={props.onClose}>
        <>
          <div className="fixed inset-0 z-30 overflow-y-auto xs:w-full xs:scrollbar-hide sm:pt-1.5 sm:pb-8 sm:mb-0">
            <div className="fixed inset-0 bg-black/40" onClick={props.onClose} aria-hidden="true" />
            <div className={classNames("flex min-h-full items-center justify-center text-center p-0")}>
              <dialog
                open
                className={classNames(
                  "relative transform overflow-visible sm:rounded-lg bg-white",
                  props.maxWidth ? props.maxWidth : "max-w-4xl",
                  !props.isDynamicWidth && "w-full",
                  "px-4 pt-5 pb-4 text-left shadow-xl sm:my-8 sm:p-6",
                  props.customPadding ? props.customPadding : "px-4 pt-5 pb-4 sm:my-8 sm:p-6",
                  props.className,
                  props.width,
                )}
                onCancel={props.onClose}
                onClick={(e) => e.stopPropagation()}
                style={{ border: "none", outline: "none", position: "relative" }}
              >
                {!props.isXMarkHidden && (
                  <div className={classNames("absolute top-0 right-0 pt-4.5 pr-4.5 block z-20", props.closeClassNames)}>
                    <button
                      type="button"
                      className="rounded-lg bg-white hover:bg-gray-200 p-1 focus:outline-none"
                      onClick={props.onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                    </button>
                  </div>
                )}
                {props.children}
              </dialog>
            </div>
          </div>
        </>
      </ModalProvider>
    </ErrorBoundary>
  )

  return ReactDOM.createPortal(modalContent, document.body)
}
